// Admins: every envelope's status and audit trail; can void. No document access (design Q8).
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Shield } from "lucide-react";
import { useApi } from "../lib/api";
import { STATUS, shortDate } from "../lib/format";
import type { Envelope, EnvelopeStatus } from "../lib/types";
import { StatusChip, inputCls } from "../components/bits";
import { Empty, ErrorNote, Spinner, cx } from "../components/ui";

export default function Admin() {
  const { data, error, loading } = useApi<{ envelopes: Envelope[] }>("/admin/envelopes");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<EnvelopeStatus | "">("");
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (data?.envelopes ?? []).filter((e) => (!status || e.status === status) && (!n || e.title.toLowerCase().includes(n) || e.ownerEmail.includes(n) || e.ownerName.toLowerCase().includes(n)));
  }, [data, q, status]);
  const counts = useMemo(() => {
    const c: Partial<Record<EnvelopeStatus, number>> = {};
    for (const e of data?.envelopes ?? []) c[e.status] = (c[e.status] ?? 0) + 1;
    return c;
  }, [data]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 className="text-xl font-semibold flex items-center gap-2 mb-1">
        <Shield className="size-5" /> Admin
      </h1>
      <p className="text-sm text-muted mb-5">Every envelope's status and audit trail. Admins can void envelopes but can't open documents.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {(["sent", "completed", "draft", "declined"] as const).map((s) => (
          <div key={s} className="bg-surface border border-line rounded-xl px-4 py-3">
            <div className="text-xs text-muted">{STATUS[s].label}</div>
            <div className="text-2xl font-semibold tabular">{counts[s] ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative sm:w-72">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title or sender" aria-label="Search" className={cx(inputCls, "pl-9")} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value as EnvelopeStatus | "")} className={cx(inputCls, "sm:w-52")} aria-label="Status">
          <option value="">All statuses</option>
          {Object.entries(STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : !list.length ? (
        <Empty icon={<Shield className="size-10" />} title="No envelopes" />
      ) : (
        <div className="bg-surface border border-line rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">Title</th>
                <th className="px-4 py-2 font-medium">Sender</th>
                <th className="px-4 py-2 font-medium">Signed</th>
                <th className="px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((e) => (
                <tr key={e.id} className="hover:bg-surface-2">
                  <td className="px-4 py-2.5 max-w-xs">
                    <Link to={`/admin/envelopes/${e.id}`} className="font-medium text-ink hover:underline line-clamp-1">
                      {e.title}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-ink-2 whitespace-nowrap">{e.ownerName}</td>
                  <td className="px-4 py-2.5 tabular text-ink-2">{e.status === "draft" ? "—" : `${e.signedCount}/${e.signerCount}`}</td>
                  <td className="px-4 py-2.5 text-muted whitespace-nowrap">{shortDate(e.createdAt)}</td>
                  <td className="px-4 py-2.5">
                    <StatusChip status={e.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
